import React, { useEffect, useState } from "react";
import { useLang } from "./LanguageProvider";
import { Menu, X, Languages } from "lucide-react";
import { CATALOGUE_URL } from "@/lib/siteConfig";

// Use the ORIGINAL bi-color logo (teal "flow" + white "white") on a dark navbar —
// gives both colors maximum contrast and matches the brand guideline.
const LOGO_URL = "/logo/flowwhite-logo-dark.png";

const navLinks = [
  { id: "investor", key: "about" },
  { id: "gcc", key: "gcc" },
  { id: "factory", key: "factory" },
  { id: "compare", key: "compare" },
  { id: "projects", key: "projects" },
  { id: "certifications", key: "certifications" },
  { id: "contact", key: "contact" },
];

export default function Navbar() {
  const { t, lang, toggle, isRtl } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is in view, for aria-current on nav links.
  useEffect(() => {
    const ids = navLinks.map((l) => l.id);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Always strong dark navy glass — both colors of the bi-color logo stay readable at every scroll state.
  const navClass = scrolled
    ? "bg-brand-navy backdrop-blur-xl border-b border-white/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.55)]"
    : "bg-brand-navy/95 backdrop-blur-xl border-b border-white/10";

  const handleScrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${navClass}`}
    >
      <div className="container mx-auto px-6 md:px-10">
        <div ref={(el) => { if (el) document.documentElement.style.setProperty("--nav-h", `${el.offsetHeight}px`); }} className="flex items-center justify-between h-24 md:h-32 lg:h-36">
          <button
            onClick={() => handleScrollTo("hero")}
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt="FlowWhite"
              className="h-16 md:h-20 lg:h-24 w-auto select-none transition-transform duration-300 group-hover:scale-[1.03]"
              draggable="false"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-9" aria-label="Primary">
            {navLinks.map((l) => {
              const isActive = activeId === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => handleScrollTo(l.id)}
                  data-testid={`nav-link-${l.id}`}
                  aria-current={isActive ? "location" : undefined}
                  className={`text-sm tracking-wide whitespace-nowrap py-2 transition-colors duration-200 ${
                    isActive ? "text-white" : "text-white/75 hover:text-white"
                  }`}
                >
                  {t.nav[l.key]}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/80 hover:text-white border border-white/15 hover:border-white/40 rounded-full px-4 py-2 transition-all"
              aria-label="Toggle language"
            >
              <Languages size={14} strokeWidth={1.6} />
              {lang === "en" ? t.common.lang_toggle_to_ar : t.common.lang_toggle_to_en}
            </button>

            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-catalogue-btn"
              className="hidden md:inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-white bg-brand-teal hover:bg-brand-tealDark rounded-full px-5 py-2.5 transition-all"
            >
              {t.nav.catalogue}
            </a>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              className="lg:hidden text-white p-2"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-sheet"
            >
              {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div
          id="mobile-menu-sheet"
          data-testid="mobile-menu"
          style={{ position: "fixed", left: 0, right: 0, top: "var(--nav-h, 96px)", height: "calc(100vh - var(--nav-h, 96px))", zIndex: 40 }}
          className="lg:hidden bg-brand-navy border-t border-white/10 overflow-y-auto"
        >
          <div className="container mx-auto px-6 py-8 flex flex-col gap-1">
            {navLinks.map((l) => {
              const isActive = activeId === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => handleScrollTo(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  aria-current={isActive ? "location" : undefined}
                  className={`text-${isRtl ? "right" : "left"} text-base py-3 border-b border-white/5 ${
                    isActive ? "text-white" : "text-white/85 hover:text-white"
                  }`}
                >
                  {t.nav[l.key]}
                </button>
              );
            })}
            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={toggle}
                data-testid="mobile-lang-toggle"
                className="flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] text-white/85 border border-white/20 rounded-full px-4 py-3"
              >
                <Languages size={14} />
                {lang === "en" ? t.common.lang_toggle_to_ar : t.common.lang_toggle_to_en}
              </button>
              <a
                href={CATALOGUE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-catalogue-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-medium text-white bg-brand-teal hover:bg-brand-tealDark rounded-full px-4 py-3"
              >
                {t.nav.catalogue}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
